// Runtime-Polyfills VOR allen anderen Imports — der embedded /funnel läuft auf
// fremden Kundenseiten mit teils alten Browsern (iOS Safari <15.4), die
// Array/String.prototype.at nicht kennen. @bugsnag/js nutzt .at() → sonst
// "this.o.at is not a function" auf /funnel (Bugsnag-Error, eingeführt 2.7.5).
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { PostHogProvider } from '@posthog/react'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { QueryClient, defaultShouldDehydrateQuery } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { Amplify, Hub } from 'aws-amplify'
import 'core-js/features/array/at'
import 'core-js/features/string/at'
import { MotionConfig } from 'framer-motion'
import {
  get as getItem,
  clear as idbClear,
  del as removeItem,
  set as setItem
} from 'idb-keyval'
import * as React from 'react'
import { createRoot } from 'react-dom/client'
import './App.css'

import 'react-loading-skeleton/dist/skeleton.css'
import App from './App'
import './i18n'
import './index.css'
// import i18n (needs to be bundled ;))
import { BrowserRouter } from 'react-router-dom'
import awsconfig from './aws-exports'
import { Loader } from './components'
import AccessDenied from './components/access-denied'
import Maintenance from './components/maintenance'
import { QUERY_KEYS } from './constants/constants'
import { FlagsProvider } from './features/experiments'
import { prefetchFeatureFlags } from './features/experiments/use-feature-flags-query'
import {
  BUILD_ENV,
  NODE_ENV,
  createErrorLog,
  initializeFirebase,
  isNetworkError
} from './helpers'
import { getGraphqlGroupingHash } from './helpers/graphql-grouping'
import { applyBugsnagExtras } from './helpers/graphql-response'
import { getPartner } from './helpers/partner'
import { getUser } from './helpers/user'
import { useCognito } from './hooks/use-cognito'
import { useConsent } from './hooks/use-consent'
import { useI18n } from './hooks/use-i18n'
import { useRestartDashboard } from './hooks/use-restart-dashboard'
import { useSystemStatus } from './hooks/use-system-status'
import { useTrackingScripts } from './hooks/use-tracking-scripts'
import { useUrlLogoutListener } from './hooks/use-url-logout-listener'

// Initialize log collector early to capture all console logs
import './helpers/log-collector'

const { version } = require('../package.json')

initializeFirebase()

const options = {
  api_host: 'https://eu.i.posthog.com',
  defaults: '2026-01-30',
  opt_out_capturing_by_default: true // temporarily disabled
}

Amplify.configure({
  ...awsconfig,
  Analytics: { autoSessionRecord: false }
})

// Flags so frueh wie moeglich, siehe use-feature-flags-query.js
prefetchFeatureFlags()

Bugsnag.start({
  apiKey: 'c8939f4d1cc8053876ee36682765eeff',
  plugins: [new BugsnagPluginReact()],
  releaseStage: BUILD_ENV,
  appVersion: version,
  onError: (event) => {
    if (window.location.pathname === '/logout') {
      Hub.dispatch('auth', { event: 'signOut' })
      return false
    }

    if (NODE_ENV === 'development') return false

    // In-App-Browser (TikTok/Instagram/… WebViews) injizieren eigene Scripts
    // (Form-Autofill, Bridge) als Inline-Code in unseren Dokument-Kontext.
    // Crashen die, schreibt Bugsnag sie unserer Domain zu — ihre Stack-Frames
    // zeigen aber auf die Dokument-URL, nie auf unser Bundle (/static/).
    // Echte App-Fehler (auch via console.error/notifyBugsnag) haben immer einen
    // Bundle-Frame. Fehlt jeder, ist es Fremd-Script-Noise → verwerfen.
    const stacktrace = event.errors?.[0]?.stacktrace ?? []
    const hasOwnFrame = stacktrace.some(
      (frame) =>
        typeof frame.file === 'string' && frame.file.includes('/static/')
    )
    if (stacktrace.length > 0 && !hasOwnFrame) return false

    // event.errors is an array of one or more Error objects.
    // The first item in the list represents the thrown object.
    // Each subsequent item represents the error that was
    // set as the cause of the preceding one.

    // An Error object contains the following information:
    // errorMessage /	String / The message string from the error
    // ...

    if (isNetworkError({ message: event.errors[0].errorMessage })) {
      return false
    }

    if (event.unhandled) idbClear()

    const groupingHash = getGraphqlGroupingHash(event.errors[0].errorMessage)
    if (groupingHash) event.groupingHash = groupingHash

    const user_id = getUser().username
    const partner = getPartner()

    const { aws_user_pools_id, aws_cognito_region } = awsconfig
    const cognito_url = user_id
      ? 'https://eu-central-1.console.aws.amazon.com/cognito/v2/idp/user-pools/' +
        `${aws_user_pools_id}/user-management/users/details/${user_id}?region=${aws_cognito_region}`
      : null

    // Context ist Bugsnags primärer Gruppierungs-/Anzeige-Schlüssel – die
    // Route gehört hierhin (zeigt *wo* der Fehler auftrat), die Version steckt
    // in appVersion + Metadaten.
    event.context = window.location.pathname
    event.addMetadata('meta', {
      version,
      build_env: BUILD_ENV.toUpperCase(),
      partner_id: partner.id,
      partner_name: partner.name,
      user_id,
      cognito_url
    })

    applyBugsnagExtras(event)

    createErrorLog({ event })
  }
})

const originalConsoleError = console.error

const customConsoleError = (...args) => {
  originalConsoleError.apply(console, args)

  // Nur echte Error-Instanzen an Bugsnag melden. `new Error(args.join(' '))`
  // erzeugte sonst Müll-Events ([object Object]-Messages, kein echter
  // Stacktrace → falsches Grouping) und reportete jedes string-console.error
  // (auch aus Third-Party-Libs) als App-Fehler.
  const error = args.find((arg) => arg instanceof Error)
  if (error) Bugsnag.notify(error)
}

console.error = customConsoleError

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React)

const FOURTEEN_DAYS = 1000 * 60 * 60 * 24 * 14

// Nie aus dem Cache. Bis zu 14 Tage alte Flags wuerden sofort als geladen
// gelten, der Funnel froere damit eine veraltete Zuteilung ein. Der Zugang der
// anderen Standorte entscheidet ueber einen automatischen Standortwechsel und
// darf nie veraltet sein.
const NEVER_PERSISTED_QUERY_KEYS = [
  QUERY_KEYS.featureFlags[0],
  QUERY_KEYS.spacesAccess('')[0]
]

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: FOURTEEN_DAYS,
      // Abgelehnte Rechte aendern sich durch Wiederholen nicht, und jeder
      // Versuch zeigt erneut den Hinweis "kein Zugriff"
      retry: (failureCount, error) =>
        error?.message !== 'NOT_AUTHORIZED' && failureCount < 3
    },
    mutations: { gcTime: FOURTEEN_DAYS }
  }
})

const asyncStoragePersister = createAsyncStoragePersister({
  storage: { getItem, setItem, removeItem }
})

const container = document.getElementById('root')

const root = createRoot(container) // createRoot(container!) if you use TypeScript

const Root = () => {
  const { language } = useI18n()
  const { status: systemStatus } = useSystemStatus()

  useTrackingScripts(language)
  useConsent()
  useUrlLogoutListener()

  const { state: restartDashboardState, restartDashboard } =
    useRestartDashboard({ queryClient })

  const { state: cognitoStatus, cognitoUser } = useCognito({
    restartDashboard
  })

  if (systemStatus === 'INITIAL' || cognitoStatus === 'INITIAL') {
    return <Loader />
  }

  if (cognitoStatus === 'ACCESS_DENIED') {
    return <AccessDenied />
  }

  if (systemStatus === 'OFFLINE') {
    return <Maintenance prefix='offline.' support />
  }

  if (systemStatus === 'MAINTENANCE_WORK') {
    return <Maintenance prefix='maintenance_work.' />
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: asyncStoragePersister,
        buster: `${version}`,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) =>
            !NEVER_PERSISTED_QUERY_KEYS.includes(query.queryKey[0]) &&
            defaultShouldDehydrateQuery(query)
        }
      }}
    >
      <ErrorBoundary
        FallbackComponent={() => (
          <Maintenance prefix='error_unhandled.' homepage support />
        )}
      >
        <PostHogProvider
          apiKey='phc_q2vnAZBHK8SzjBTsndAYuutRTEFtkW5PHHyZsps7eYVe'
          options={options}
        >
          {restartDashboardState === 'RESTART' ? (
            <Loader />
          ) : (
            // Ueber App, damit auch die public Route /funnel Flags bekommt
            <FlagsProvider>
              <App {...{ language, cognitoUser, restartDashboard }} />
            </FlagsProvider>
          )}
          <ReactQueryDevtools initialIsOpen={false} />
        </PostHogProvider>
      </ErrorBoundary>
    </PersistQueryClientProvider>
  )
}

// Bei „Bewegung reduzieren“ im System laufen framer-motion-Animationen ohne
// Transform und Layout, nur mit Opacity. Gilt für das ganze Dashboard.
root.render(
  <BrowserRouter>
    <MotionConfig reducedMotion='user'>
      <Root />
    </MotionConfig>
  </BrowserRouter>
)

// # Strict Mode
//
// In React 18, the strict mode simulates mounting, unmounting,
// and remounting the component with a previous state.
//
// For instance, when a user tabs away from a screen and back,
// React should be able to display the previous screen quickly.
// To accomplish this, React would unmount and remount trees using
// the previous component state as before.
//
// This feature improves React apps’ performance but requires components
// are resilient to the effect of being mounted and unmounted multiple times.
